import { Form, Slider, Spin, Switch } from 'antd'
import ListContentLayout from 'layout/ListContentLayout'
import { FC, ReactNode, useEffect, useState } from 'react'
import { TSmartAlertPost } from 'features/automation/types'
import {
 useGetSmartListingAlertQuery,
 useSmartListingStoreMutation,
} from 'features/automation/api'
import { useGlobal } from 'raducer/global'
import {
 AiBanner,
 AiCard,
 AiFooter,
 AiPageHeader,
 TBandConfig,
} from '../components/AiModule'

const TRIGGER_CFG: TBandConfig = {
 min: 0,
 max: 50,
 labels: ['Low', 'Moderate', 'High', 'Very High'],
 engageLabel: 'Engagement level',
}

const REC_TIPS: Record<string, string> = {
 trigger_on_views: 'AI: Moderate recommended',
 price_drop_alert_views: 'AI: High recommended',
 open_house_alert_views: 'AI: Low recommended',
}

const initialValues: TSmartAlertPost = {
 trigger_on_views: 0,
 price_drop_alert_views: 0,
 open_house_alert_views: 0,
 viewed_sold: false,
 viewed_price_change: false,
 viewed_open_house: true,
 status: false,
 smart_listing_pause: true,
}

const SmartListingAlert: FC = () => {
 const { global } = useGlobal()
 const [smartListingStore, { isLoading }] = useSmartListingStoreMutation()
 const [smartListingStoreAll, { isLoading: isLoadingAll }] =
  useSmartListingStoreMutation()
 const [status, setStatus] = useState(false)
 const {
  data,
  refetch,
  isLoading: pgLoading,
 } = useGetSmartListingAlertQuery(null)
 const [form] = Form.useForm<TSmartAlertPost>()

 const triggerViews = Form.useWatch('trigger_on_views', form) ?? 0
 const priceViews = Form.useWatch('price_drop_alert_views', form) ?? 0
 const openViews = Form.useWatch('open_house_alert_views', form) ?? 0

 useEffect(() => {
  form.setFieldsValue(data ? { ...initialValues, ...data } : initialValues)
  if (data) setStatus(data.status === 1)
 }, [data])

 const buildPayload = (val: boolean): TSmartAlertPost => ({
  ...form.getFieldsValue(),
  status: val ? 1 : 0,
 })

 const handleToggle = async (val: boolean) => {
  setStatus(val)
  await smartListingStore(buildPayload(val)).unwrap()
  refetch()
 }

 const handleSave = async () => {
  const values = await form.validateFields()
  await smartListingStoreAll({
   ...values,
   status: status ? 1 : 0,
  }).unwrap()
  refetch()
 }

 const handleCancel = () => {
  form.setFieldsValue(data ? { ...initialValues, ...data } : initialValues)
 }

 return (
  <div className="smart-engegement-page">
   <div className="smart-engegement-page-body">
    <Spin spinning={pgLoading}>
     <Form form={form} initialValues={initialValues}>
      <div className={`ai-module-page${!status ? ' disabled' : ''}`}>
       <AiPageHeader
        title="AI Listing Intelligence"
        toggleLabel="AI Listing Intelligence"
        status={status}
        loading={isLoading}
        onToggle={handleToggle}
       />

       <AiBanner title="Automated Listing Alert System">
        <p>
         AI Listing Intelligence automatically assigns relevant listing alerts —
         New Listings, Price Drops
         {global.open_house ? ', Open Houses' : ''} — based on each lead&apos;s
         property views, favorites, and search behavior.
        </p>
        <p>
         Alerts adapt to engagement: frequency reduces or pauses when interest
         wanes, and resumes when a lead returns. You can customize trigger
         thresholds, manually pause alerts for specific users, and refine
         filters at any time for a more personalized experience.
        </p>
       </AiBanner>

       <AiCard
        title="AI Engagement Detection"
        tag="Smart Triggers"
        freqNote="⚡ Alert frequency &amp; timing adapts based on engagement"
        sectionNote='Note: Set to "Low" if you wish not to assign the alert for that type.'
       >
        <div className="ai-section-body">
         <TriggerBlock
          title="Assign a New Listing Alert when"
          fieldName="trigger_on_views"
          recTip={REC_TIPS.trigger_on_views}
          bandLabel={TRIGGER_CFG.labels[getBandIdx(triggerViews)]}
          disabled={!status}
         />
         <TriggerBlock
          title="Assign a Price Drop Alert when"
          fieldName="price_drop_alert_views"
          recTip={REC_TIPS.price_drop_alert_views}
          bandLabel={TRIGGER_CFG.labels[getBandIdx(priceViews)]}
          disabled={!status}
         />
         {global.open_house && (
          <TriggerBlock
           title="Assign an Open House Alert when"
           fieldName="open_house_alert_views"
           recTip={REC_TIPS.open_house_alert_views}
           bandLabel={TRIGGER_CFG.labels[getBandIdx(openViews)]}
           disabled={!status}
          />
         )}
        </div>
       </AiCard>

       <AiCard title="AI Property Activity Alerts" tag="Notify Lead">
        <div className="ai-section-body">
         {global.user_type_sold && (
          <NotifyItem icon="🏠" text="The property viewed has been sold">
           <Form.Item name="viewed_sold" noStyle valuePropName="checked">
            <Switch disabled={!status} />
           </Form.Item>
          </NotifyItem>
         )}
         <NotifyItem
          icon="💲"
          text="The property viewed has a price change (Drop / Hike)"
         >
          <Form.Item name="viewed_price_change" noStyle valuePropName="checked">
           <Switch disabled={!status} />
          </Form.Item>
         </NotifyItem>
         {global.open_house && (
          <NotifyItem icon="💎" text="The property viewed has an Open House">
           <Form.Item name="viewed_open_house" noStyle valuePropName="checked">
            <Switch disabled={!status} />
           </Form.Item>
          </NotifyItem>
         )}
        </div>
       </AiCard>

       <AiCard title="AI Conflict Prevention" tag="Auto-Pause">
        <div className="ai-section-body">
         <div className="ai-pause-block">
          <div className="ai-pause-icon">⚠️</div>
          <div style={{ flex: 1 }}>
           <div className="ai-pause-title">
            Automatically pauses to avoid duplicate alerts
           </div>
           <div className="ai-pause-desc">
            When a new alert (New Listing / Price Drop
            {global.open_house ? ' / Open House' : ''}) is assigned from the
            website or from the CRM, AI automatically pauses overlapping alert
            sequences to prevent alert fatigue and ensure a consistent lead
            experience.
           </div>
          </div>
          <Form.Item name="smart_listing_pause" noStyle valuePropName="checked">
           <Switch disabled={!status} />
          </Form.Item>
         </div>
        </div>
       </AiCard>
      </div>
     </Form>
    </Spin>
   </div>
   {status && (
    <AiFooter
     saving={isLoadingAll}
     onCancel={handleCancel}
     onSave={handleSave}
    />
   )}
  </div>
 )
}

export default SmartListingAlert

// ── Helpers ──────────────────────────────────────────────────────────

function getBandIdx(value: number): number {
 const pct = value / 50
 if (pct < 0.25) return 0
 if (pct < 0.5) return 1
 if (pct < 0.75) return 2
 return 3
}

// ── Sub-components ───────────────────────────────────────────────────

const TriggerBlock: FC<{
 title: string
 fieldName: string
 recTip: string
 bandLabel: string
 disabled: boolean
}> = ({ title, fieldName, recTip, bandLabel, disabled }) => (
 <div className="ai-trigger-block">
  <div className="ai-trigger-label-row">
   <span className="ai-trig-title">{title}</span>
   <span className="ai-rec-chip">{recTip}</span>
  </div>
  <div className="ai-engage-label">Engagement level</div>
  <div className="ai-slider-row">
   <Form.Item name={fieldName} noStyle>
    <Slider
     min={0}
     max={50}
     disabled={disabled}
     style={{ flex: 1 }}
     tipFormatter={null}
    />
   </Form.Item>
   <span className="ai-slider-val">{bandLabel}</span>
  </div>
  <div className="ai-slider-sublabels">
   <span>Low</span>
   <span>Moderate</span>
   <span>High</span>
   <span>Very High</span>
  </div>
 </div>
)

const NotifyItem: FC<{ icon: string; text: string; children: ReactNode }> = ({
 icon,
 text,
 children,
}) => (
 <div className="ai-notify-item">
  <div className="ai-notify-icon">{icon}</div>
  <span className="ai-notify-text">{text}</span>
  {children}
 </div>
)
